export type Bilingual = {
  en: string
  fr: string
}

export type ApproachVisualId =
  | 'entrypoints'
  | 'identity'
  | 'atna'
  | 'systems'
  | 'action'

export type ApproachStep = {
  num: string
  visual: ApproachVisualId
  label: Bilingual
  headline: Bilingual
  items: Bilingual[]
  featured?: boolean
}

export const approachIntro = {
  eyebrow: {
    en: 'Our approach',
    fr: 'Notre approche',
  },
  headlineLead: {
    en: 'Every interaction.',
    fr: 'Chaque interaction.',
  },
  // Second line, split so one word carries the indigo accent.
  headlineBefore: {
    en: 'One ',
    fr: 'Une fondation de ',
  },
  headlineAccent: {
    en: 'trusted',
    fr: 'confiance',
  },
  headlineAfter: {
    en: ' foundation.',
    fr: '.',
  },
  intro: {
    en: 'One platform connects identity, context, intelligence and systems — so every interaction can become action.',
    fr: 'Une seule plateforme connecte l’identité, le contexte, l’intelligence et les systèmes — pour que chaque interaction devienne une action.',
  },
}

export const approachSteps: ApproachStep[] = [
  {
    num: '01',
    visual: 'entrypoints',
    label: {
      en: 'Every entry point',
      fr: 'Chaque point d’entrée',
    },
    headline: {
      en: 'Meet people where they are.',
      fr: 'Rejoindre les gens là où ils sont.',
    },
    items: [
      { en: 'Website', fr: 'Site web' },
      { en: 'Mobile', fr: 'Mobile' },
      { en: 'Voice', fr: 'Voix' },
      { en: 'Services', fr: 'Services' },
      { en: 'AI', fr: 'IA' },
    ],
  },
  {
    num: '02',
    visual: 'identity',
    featured: true,
    label: {
      en: 'Verified identity & context',
      fr: 'Identité vérifiée et contexte',
    },
    headline: {
      en: 'Know who. Understand what matters.',
      fr: 'Savoir qui. Comprendre ce qui compte.',
    },
    items: [
      { en: 'Identity', fr: 'Identité' },
      { en: 'Relationships', fr: 'Relations' },
      { en: 'Consent', fr: 'Consentement' },
      { en: 'Rights', fr: 'Droits' },
    ],
  },
  {
    num: '03',
    visual: 'atna',
    label: {
      en: 'ATNA · Agentic intelligence',
      fr: 'ATNA · Intelligence agentique',
    },
    headline: {
      en: 'Understand. Orchestrate. Verify.',
      fr: 'Comprendre. Orchestrer. Vérifier.',
    },
    items: [
      { en: 'Intent', fr: 'Intention' },
      { en: 'Context', fr: 'Contexte' },
      { en: 'Knowledge', fr: 'Connaissances' },
      { en: 'Rules', fr: 'Règles' },
    ],
  },
  {
    num: '04',
    visual: 'systems',
    label: {
      en: 'Connected services & systems',
      fr: 'Services et systèmes connectés',
    },
    headline: {
      en: 'Make systems work together.',
      fr: 'Faire travailler les systèmes ensemble.',
    },
    items: [
      { en: 'APIs', fr: 'API' },
      { en: 'Business systems', fr: 'Systèmes d’affaires' },
      { en: 'Data', fr: 'Données' },
      { en: 'Transactions', fr: 'Transactions' },
    ],
  },
  {
    num: '05',
    visual: 'action',
    label: {
      en: 'Trusted action',
      fr: 'Action de confiance',
    },
    headline: {
      en: 'From intent to outcome.',
      fr: 'De l’intention au résultat.',
    },
    items: [
      { en: 'Recommend', fr: 'Recommander' },
      { en: 'Authorize', fr: 'Autoriser' },
      { en: 'Execute', fr: 'Exécuter' },
      { en: 'Measure', fr: 'Mesurer' },
    ],
  },
]

export const trustLayer = {
  eyebrow: {
    en: 'ATNA — Trusted intelligence',
    fr: 'ATNA — Intelligence de confiance',
  },
  headlineBefore: {
    en: 'From intent to action, with ',
    fr: 'De l’intention à l’action, en toute ',
  },
  headlineAccent: {
    en: 'confidence.',
    fr: 'confiance.',
  },
  steps: [
    {
      title: { en: 'Understand', fr: 'Comprendre' },
      caption: {
        en: 'Turn intent into context.',
        fr: 'Transformer l’intention en contexte.',
      },
    },
    {
      title: { en: 'Verify', fr: 'Vérifier' },
      caption: {
        en: 'Validate information and rules.',
        fr: 'Valider les informations et les règles.',
      },
    },
    {
      title: { en: 'Authorize', fr: 'Autoriser' },
      caption: {
        en: 'Apply permissions and governance.',
        fr: 'Appliquer les autorisations et la gouvernance.',
      },
    },
    {
      title: { en: 'Act', fr: 'Agir' },
      caption: {
        en: 'Execute with trusted systems.',
        fr: 'Exécuter avec des systèmes de confiance.',
      },
    },
  ],
}

export const approachVisualLabels = {
  identityCard: {
    name: { en: 'Verified identity', fr: 'Identité vérifiée' },
    status: { en: 'Verified', fr: 'Vérifiée' },
  },
  atnaCaption: {
    en: 'Turning intent into action.',
    fr: 'Transformer l’intention en action.',
  },
  actionDone: {
    en: 'Action completed',
    fr: 'Action accomplie',
  },
}
